import Raiox from '@/components/medico/raiox/index.vue'
import PdfExam from '@/components/medico/pdfExam/index.vue'
import feedback from '@/components/medico/feedback/index.vue'
import Analyst from '@/components/medico/analyst/index.vue'
import CONSTANTS from '@/shared/constants/constants'
import MedicalReportMixin from '@/shared/mixins/medical-report.mixin'
import AuthMixin from '@/shared/mixins/auth.mixin'
import PhysicianHeartbeatMixin from '@/shared/mixins/physician-heartbeat.mixin'
import { mapGetters, mapActions } from 'vuex'

const HEARTBEAT_INTERVAL_IN_MILLISECONDS = 60000

export default {
  name: 'medico',
  components: {
    Raiox,
    PdfExam,
    feedback,
    Analyst
  },
  mixins: [
    MedicalReportMixin,
    AuthMixin,
    PhysicianHeartbeatMixin
  ],
  data () {
    return {
      exam: null,
      SPECIALITY: CONSTANTS.SPECIALITY,
      STATUS: CONSTANTS.STATUS,
      isAria: false,
      isOit: false,
      specialtiesAria: [],
      user: {},
      role: '',
      heartbeatInterval: null,
      hasActivitySinceLastHeartbeat: false,
      lastActivityAt: null,
      heartbeatListenersRegistered: false,
      heartbeatMouseMoveHandler: null,
      hasNoExamsAvailable: false
    }
  },
  computed: {
    isSingleSession () {
      const isSingleSession = this.getIsSingleSession()

      if (isSingleSession && !this.exam) {
        this.getExamQueue()
      }
      return isSingleSession
    },
    isAnalyst () {
      return this.role === CONSTANTS.ROLES.ANALYST
    }
  },
  mounted () {
    this.$root.$on('refreshTimeout', this.setPrioritasUpdateTime)
    this.specialtiesAria.push(
      this.SPECIALITY.RAIOX,
      this.SPECIALITY.TOMOGRAFIA,
      this.SPECIALITY.RESSONANCIA,
      this.SPECIALITY.MAMOGRAFIA,
      this.SPECIALITY.DENSITOMETRIA
    )
    this.user = this.getUser()
    this.role = this.getFirstRole()
    this.startPhysicianHeartbeat()

    if (this.isSingleSession === null) {
      this.$root.$emit('showLoading')
      this.startVerifySession()
    }
  },
  beforeDestroy () {
    this.stopPhysicianHeartbeat()
  },
  methods: {
    ...mapGetters([
      'getIsSingleSession'
    ]),
    ...mapActions([
      'setDontReload',
      'setSingleSession'
    ]),
    startPhysicianHeartbeat () {
      if (!this.isHeartbeatRouteEligible() || this.heartbeatInterval) {
        return
      }

      this.registerHeartbeatListeners()
      this.heartbeatInterval = setInterval(() => {
        this.sendPhysicianHeartbeat()
      }, HEARTBEAT_INTERVAL_IN_MILLISECONDS)
    },
    stopPhysicianHeartbeat () {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
      this.unregisterHeartbeatListeners()
      this.hasActivitySinceLastHeartbeat = false
      this.lastActivityAt = null
    },
    registerHeartbeatListeners () {
      if (this.heartbeatListenersRegistered) {
        return
      }

      this.getHeartbeatListeners().forEach(({ eventName, handler }) => {
        document.addEventListener(eventName, handler, false)
      })
      this.heartbeatListenersRegistered = true
    },
    unregisterHeartbeatListeners () {
      if (!this.heartbeatListenersRegistered) {
        return
      }

      this.getHeartbeatListeners().forEach(({ eventName, handler }) => {
        document.removeEventListener(eventName, handler, false)
      })
      this.heartbeatListenersRegistered = false
    },
    getHeartbeatListeners () {
      if (!this.heartbeatMouseMoveHandler) {
        this.heartbeatMouseMoveHandler = this.markPhysicianActivity
      }

      return [
        {
          eventName: 'mousemove',
          handler: this.heartbeatMouseMoveHandler
        }
      ]
    },
    markPhysicianActivity () {
      if (!this.isHeartbeatRouteEligible()) {
        return
      }

      this.hasActivitySinceLastHeartbeat = true
      this.lastActivityAt = new Date()
    },
    async sendPhysicianHeartbeat () {
      if (!this.shouldSendPhysicianHeartbeat()) {
        return false
      }

      const payload = this.getHeartbeatPayload()

      if (!payload) {
        return false
      }

      try {
        await this.postPhysicianHeartbeat(payload)
        this.hasActivitySinceLastHeartbeat = false
        return true
      } catch (error) {
        return false
      }
    },
    shouldSendPhysicianHeartbeat () {
      return this.hasActivitySinceLastHeartbeat
    },
    getHeartbeatPayload () {
      const currentExamId = this.getHeartbeatExamId()
      const activityOriginId = this.getHeartbeatOriginId()

      if (typeof currentExamId === 'undefined' || !activityOriginId) {
        return null
      }

      return {
        currentExamId,
        activityOriginId
      }
    },
    getHeartbeatExamId () {
      if (this.hasNoExamsAvailable) {
        return null
      }
      return this.exam && this.exam.examId ? this.exam.examId : undefined
    },
    getHeartbeatOriginId () {
      if (!this.isHeartbeatRouteEligible()) {
        return null
      }

      return CONSTANTS.PHYSICIAN_ACTIVITY_ORIGIN.LAUDAR
    },
    isHeartbeatRouteEligible () {
      return this.$route && this.$route.name === 'medicoLaudar'
    },
    startVerifySession () {
      setTimeout(() => {
        if (this.isSingleSession === null) {
          this.setSingleSession(true)
        }
      }, 2000)
    },
    logout () {
      this.logoutUser()
    },
    refresh () {
      this.setDontReload(true)
      this.setSingleSession(true)
      this.$socket.emit('reload')
    },
    async getExamQueue () {
      this.$root.$emit('showLoading')
      try {
        const storedValidation = sessionStorage.getItem('certificateValidation')

        if (storedValidation) {
          const parsedValidation = JSON.parse(storedValidation)
          if (parsedValidation.token && parsedValidation.tokenExpiration) {
            const tokenExpiration = new Date(parsedValidation.tokenExpiration)

            if (new Date() >= tokenExpiration) {
              await this.getValidateCertificate(true)
            }
          } else {
            await this.getValidateCertificate(true)
          }
        } else {
          await this.getValidateCertificate(true)
        }
        const res = await this.getQueueFirstItem()
        if (res.status === 204) {
          this.hasNoExamsAvailable = true
          this.hasActivitySinceLastHeartbeat = true
          this.sendPhysicianHeartbeat()
          const data = {
            hasMoreExams: false,
            type: 'dontHaveExams'
          }
          return this.showFeedback(data)
        }
        this.hasNoExamsAvailable = false
        this.exam = res.data.data
        this.exam.observations = this.exam.observations.filter((obs) => obs.content && obs.content.trim().length >= 3)
        this.isOit = this.exam.specialityId === CONSTANTS.SPECIALITY.RAIOXOIT
        this.isAria = this.specialtiesAria.includes(this.exam.specialityId) && this.exam.pixeonUid != null

        if (!this.exam.labels || !this.exam.labels.length) {
          const examId = this.exam.examId
          this.requestGet(`/core/Exams/${examId}`)
            .then((detailsResponse) => {
              if (this.exam?.examId !== examId) {
                return
              }
              const details = detailsResponse && detailsResponse.data
              if (details && details.labels && details.labels.length) {
                this.$set(this.exam, 'labels', details.labels)
              }
            })
            .catch((error) => {
              console.error('Erro ao buscar labels do exame:', error)
            })
        }

        if (!this.exam.masks) {
          this.exam.masks = []
        }

        const retificationStatus = [this.STATUS.AGUARDANDO_RETIFICACAO, this.STATUS.RETIFICANDO]
        if (retificationStatus.includes(this.exam.status)) {
          this.exam.reportStatus = this.exam.isAlterado ? CONSTANTS.REPORT_STATUS.ALTERADO : CONSTANTS.REPORT_STATUS.NORMAL
        }
        if (this.exam.hasInformation || this.exam.observations.length && !this.isOit) {
          const data = {
            hasMoreExams: true,
            type: 'warnMessage'
          }
          this.showFeedback(data)
        }
        if (this.exam.retification && Object.keys(this.exam.retification).length) {
          const data = {
            hasMoreExams: true,
            type: 'rectificationMessage'
          }
          this.showFeedback(data)
        }
      } finally {
        this.$root.$emit('hideLoading')
      }
    },
    nextExam () {
      this.exam = {}
      this.getExamQueue()
    },
    showFeedback (data) {
      this.$refs.feedback.showFeedback(data.hasMoreExams, data.type)
    },
    setPrioritasUpdateTime (data) {
      this.exam.prioritasUpdateTime = data
    }
  }
}
